import { apiService } from './api.service';
import { API_ENDPOINTS } from '../config/api';

export interface Especialidad {
    id: number;
    nombre: string;
}

export interface Bodega {
    id: string;
    nombre: string;
}

export interface Diagnostico {
    codigo: string;
    nombre: string;
}

class GenericosService {
    /**
     * Obtiene la lista de especialidades médicas.
     * @param estado Estado de las especialidades (default: true)
     * @returns Lista de especialidades
     */
    async getEspecialidades(estado: boolean | string = true): Promise<Especialidad[]> {
        return apiService.get<Especialidad[]>(`${API_ENDPOINTS.GENERICOS_ESPECIALIDADES}?estado=${estado}`);
    }

    async getNextIdEspecialidad(): Promise<{ next_id: number }> {
        return apiService.get<{ next_id: number }>(`${API_ENDPOINTS.GENERICOS_ESPECIALIDADES}/next-id`);
    }

    async createEspecialidad(nombre: string): Promise<any> {
        return apiService.post<any>(API_ENDPOINTS.GENERICOS_ESPECIALIDADES, { nombre });
    }

    /**
     * Obtiene la lista de bodegas.
     */
    async getBodegas(): Promise<Bodega[]> {
        return apiService.get<Bodega[]>(API_ENDPOINTS.GENERICOS_BODEGAS);
    }

    /**
     * Obtiene la lista de laboratorios.
     */
    async getLaboratorios(search: string = ""): Promise<any[]> {
        return apiService.get<any[]>(`${API_ENDPOINTS.GENERICOS_LABORATORIOS}?search=${encodeURIComponent(search)}`);
    }

    /**
     * Busca diagnósticos por código o nombre.
     * @param query Texto de búsqueda
     */
    async searchDiagnosticos(query: string): Promise<Diagnostico[]> {
        return apiService.get<Diagnostico[]>(`${API_ENDPOINTS.GENERICOS_DIAGNOSTICOS}?search=${encodeURIComponent(query)}`);
    }

    /**
     * Obtiene la lista de formas farmacéuticas.
     */
    async getFormasFarmaceuticas(): Promise<any[]> {
        return apiService.get<any[]>(API_ENDPOINTS.GENERICOS_FORMAS_FARMACEUTICAS);
    }

    /**
     * Obtiene la lista de unidades de medida para medicamentos.
     */
    async getUnidadesMedidasMed(): Promise<any[]> {
        return apiService.get<any[]>(API_ENDPOINTS.GENERICOS_UNIDADES_MEDIDAS_MED);
    }

    /**
     * Obtiene la lista de vías de administración de medicamentos.
     */
    async getViasAdministracion(): Promise<any[]> {
        return apiService.get<any[]>(API_ENDPOINTS.GENERICOS_VIAS_ADMINISTRACION);
    }

    /**
     * Obtiene la lista de departamentos.
     */
    async getDepartamentos(): Promise<any[]> {
        return apiService.get<any[]>(API_ENDPOINTS.GENERICOS_DEPARTAMENTOS);
    }

    /**
     * Obtiene la lista de municipios, dada una query o id de departamento opcional.
     */
    async getMunicipios(departamentoId?: string): Promise<any[]> {
        return apiService.get<any[]>(`${API_ENDPOINTS.GENERICOS_MUNICIPIOS}`, true, {
            params: {
                cod_dpto: departamentoId
            }
        });
    }

    /**
     * Obtiene la lista de tipos de identificaciones.
     */
    async getTiposIdentificaciones(): Promise<any[]> {
        return apiService.get<any[]>(API_ENDPOINTS.GENERICOS_TIPOS_IDENTIFICACIONES);
    }

    /**
     * Obtiene la lista de regimenes de salud.
     */
    async getRegimenesSalud(): Promise<any[]> {
        return apiService.get<any[]>(API_ENDPOINTS.GENERICOS_REGIMENES_SALUD);
    }

    /**
     * Obtiene la lista de parentescos.
     */
    async getParentescos(): Promise<any[]> {
        return apiService.get<any[]>(API_ENDPOINTS.GENERICOS_PARENTESCOS);
    }

    /**
     * Obtiene la lista de poblaciones clave.
     */
    async getPoblacionesClave(): Promise<any[]> {
        return apiService.get<any[]>(API_ENDPOINTS.GENERICOS_POBLACIONES_CLAVE);
    }

    /**
     * Obtiene la lista de grupos étnicos.
     */
    async getGruposEtnicos(): Promise<any[]> {
        return apiService.get<any[]>(API_ENDPOINTS.GENERICOS_GRUPOS_ETNICOS);
    }

    /**
     * Obtiene la lista de estados civiles.
     */
    async getEstadosCiviles(): Promise<any[]> {
        return apiService.get<any[]>(API_ENDPOINTS.GENERICOS_ESTADOS_CIVILES);
    }

    /**
     * Obtiene la lista de entidades o EPS.
     */
    async getEntidades(): Promise<any[]> {
        return apiService.get<any[]>(API_ENDPOINTS.GENERICOS_ENTIDADES);
    }

    /**
     * Obtiene la información de categoría contributivo dado el código de categoría del paciente.
     * Solo aplica para pacientes con régimen contributivo (ID 1).
     * @param categoria Código de categoría (ej: 'A', 'B', 'C')
     */
    async getCategoriaContributivo(categoria: string): Promise<any> {
        return apiService.get<any>(
            `${API_ENDPOINTS.GENERICOS_CATEGORIA_CONTRIBUTIVO}?categoria=${encodeURIComponent(categoria)}`
        );
    }
}

export const genericosService = new GenericosService();
